import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { usersService } from '@/services/UsersService'

// Deriva el estado de UI a partir de la membresía de empresa (company_users.status).
// REJECTED → 'rejected' (rechazó la invitación), INVITED → 'pending',
// DISABLED → 'inactive' (pausado por el dueño), ACTIVE → 'active'.
function deriveWorkerStatus(worker) {
  const membershipStatus = (worker.user?.company_membership_status || '').toUpperCase()
  if (membershipStatus === 'REJECTED') return 'rejected'
  if (membershipStatus === 'INVITED') return 'pending'
  if (membershipStatus === 'DISABLED') return 'inactive'
  if (membershipStatus === 'ACTIVE') return 'active'
  if (worker.status) return worker.status
  return !worker.user ? 'pending' : (worker.is_active ? 'active' : 'inactive')
}

export const useUsersStore = defineStore('users', () => {
  // State
  const users = ref([])
  const isLoading = ref(false)
  const error = ref(null)

  // Getters
  const activeUsers = computed(() => 
    users.value.filter(user => user.status === 'active')
  )

  const pendingUsers = computed(() => 
    users.value.filter(user => user.status === 'pending')
  )

  const adminUsers = computed(() => 
    users.value.filter(user => user.role === 'admin' || user.role === 'worker-admin')
  )

  const userCount = computed(() => users.value.length)

  const getUserById = computed(() => (id) => 
    users.value.find(user => user.id === id)
  )

  const getUsersByRole = computed(() => (role) => 
    users.value.filter(user => user.role === role)
  )

  // Actions
  async function fetchUsers(companyId) {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await usersService.getAll(companyId)
      const rawWorkers = response.data || response || []

      users.value = rawWorkers.map(worker => ({
        id: worker.id,
        user_id: worker.user_id,
        name: worker.full_name,
        full_name: worker.full_name,
        email: worker.user?.email || '',
        phone: worker.phone || '',
        position: worker.position || '',
        role: worker.user?.role?.name || worker.role?.name || worker.role_name || '',
        status: deriveWorkerStatus(worker),
        lastAccess: 'Hace tiempo',
        avatarColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
        hire_date: worker.hire_date,
        salary: worker.salary,
        commission_rate: worker.commission_rate,
        notes: worker.notes,
        availabilityBlocks: worker.availability_blocks || worker.availabilityBlocks || [],
        is_primary: worker.companies?.[0]?.relation?.is_primary || false
      }))
    } catch (err) {
      error.value = 'Error al cargar usuarios'
      console.error('Error fetching users:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function createUser(companyId, userData) {
    try {
      const createPayload = {
        email: userData.email.trim(),
        full_name: (userData.full_name || userData.name || '').trim(),
        phone: userData.phone || '',
        position: userData.position || '',
        hire_date: userData.hire_date || '',
        salary: userData.salary || '',
        commission_rate: userData.commission_rate || '',
        notes: userData.notes || '',
        role_name: userData.role || 'read',
        company_id: companyId,
        is_primary: userData.isPrimary || false
      }

      const response = await usersService.create(companyId, createPayload)
      const newWorker = response.data || response

      const mappedUser = {
        id: newWorker.id,
        user_id: newWorker.user_id,
        name: newWorker.full_name || createPayload.full_name,
        full_name: newWorker.full_name || createPayload.full_name,
        email: newWorker.user?.email || userData.email,
        phone: newWorker.phone || userData.phone,
        position: newWorker.position || userData.position,
        role: newWorker.user?.role?.name || userData.role || '',
        status: 'pending',
        lastAccess: 'Nunca',
        avatarColor: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
        hire_date: newWorker.hire_date,
        salary: newWorker.salary,
        commission_rate: newWorker.commission_rate,
        notes: newWorker.notes,
        is_primary: newWorker.is_primary || false
      }

      users.value.push(mappedUser)
      return mappedUser
    } catch (err) {
      console.error('Error creating user:', err)
      throw err
    }
  }

 async function updateUser(userId, companyId, userData) {
  try {
    const updatePayload = {
      name: userData.name?.trim() || userData.full_name || '',
      phone: userData.phone || '',
      position: userData.position || '',
      salary: userData.salary || '',
      commission_rate: userData.commission_rate || '',
      notes: userData.notes || ''
    }

    if (userData.password) {
      updatePayload.password = userData.password
    }

    const response = await usersService.update(userId, companyId, updatePayload)
    const updatedWorker = response.data || response
    const currentUser = users.value.find(u => u.id === userId)
    if (userData.role && currentUser?.user_id && userData.role !== currentUser.role) {
      await usersService.assignRole(currentUser.user_id, userData.role)
      
      if (!updatedWorker.user) updatedWorker.user = {}
      updatedWorker.user.role = { name: userData.role }
    }

    const mappedUser = {
      ...currentUser,
      name: updatedWorker.full_name || updatePayload.name,
      full_name: updatedWorker.full_name || updatePayload.name,
      phone: updatedWorker.phone || updatePayload.phone,
      position: updatedWorker.position || updatePayload.position,
      role: updatedWorker.user?.role?.name || userData.role || currentUser.role,
      salary: updatedWorker.salary ?? updatePayload.salary,
      commission_rate: updatedWorker.commission_rate ?? updatePayload.commission_rate,
      notes: updatedWorker.notes || updatePayload.notes,
      hire_date: updatedWorker.hire_date || currentUser.hire_date
    }

    const index = users.value.findIndex(u => u.id === userId)
    if (index !== -1) {
      users.value[index] = mappedUser
    }
    return mappedUser
  } catch (err) {
    console.error('Error updating user:', err)
    throw err
  }
}

  async function deleteUser(userId, body = {}) {
    try {
      const response = await usersService.delete(userId, body)
      users.value = users.value.filter(u => u.id !== userId)
      return response?.data ?? response
    } catch (err) {
      console.error('Error deleting user:', err)
      throw err
    }
  }

  async function cancelInvitation(userId) {
    try {
      const response = await usersService.cancelInvitation(userId)
      users.value = users.value.filter(u => u.id !== userId)
      return response?.data ?? response
    } catch (err) {
      console.error('Error canceling invitation:', err)
      throw err
    }
  }

  async function sendInvitation(userId) {
    try {
      await usersService.sendInvitation(userId)
      const user = users.value.find(u => u.id === userId)
      if (user) {
        user.invitationSent = true
      }
    } catch (err) {
      console.error('Error sending invitation:', err)
      throw err
    }
  }

  async function updateUserStatus(userId, status, body = {}) {
    try {
      const response = await usersService.updateStatus(userId, status, body)
      const user = users.value.find(u => u.id === userId)
      if (user) {
        user.status = status
      }
      return response?.data ?? user
    } catch (err) {
      console.error('Error updating user status:', err)
      throw err
    }
  }

  // Re-invita a un miembro que rechazó: vuelve a 'pending' (membresía INVITED)
  async function reinvite(userId) {
    try {
      await usersService.reinvite(userId)
      const user = users.value.find(u => u.id === userId)
      if (user) {
        user.status = 'pending'
      }
      return user
    } catch (err) {
      console.error('Error reinviting user:', err)
      throw err
    }
  }

  // Validaciones
  function validateUser(userData) {
    const errors = []
    
    if (!userData.name || userData.name.trim().length < 2) {
      errors.push('El nombre debe tener al menos 2 caracteres')
    }
    
    if (!userData.email || !userData.email.includes('@')) {
      errors.push('El email no es válido')
    }
    
    if (!userData.role) {
      errors.push('Debe seleccionar un rol')
    }
    
    // Verificar email único
    const email = (userData.email || '').toString().trim().toLowerCase()
    const editingId = userData.id
    const existingUser = users.value.find(u => {
      const uEmail = (u.email || '').toString().trim().toLowerCase()
      return uEmail && email && uEmail === email && u.id !== editingId
    })
    
    if (existingUser) {
      errors.push('Ya existe un usuario con ese email')
    }
    
    return errors
  }

  function $reset() {
    users.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    users,
    isLoading,
    error,
    
    // Getters
    activeUsers,
    pendingUsers,
    adminUsers,
    userCount,
    getUserById,
    getUsersByRole,
    
    // Actions
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    cancelInvitation,
    sendInvitation,
    updateUserStatus,
    reinvite,
    validateUser,
    $reset
  }
})
